import { NVLVendor } from '../../main/containers/national-vendor-list'
import { Company, ListVendor, SimpleVendor } from '../Company'
import {
    AreaConfig,
    BaseAreaConfig,
    UnitConfig,
    UnitConfigAreaCounts,
} from '../Config'
import { Service, ServiceCategoryCode } from '../Services'
import { WorkorderGroup } from '../WorkOrder'
import { Address, IdentifiableNamedObject, IdentifiableObject } from '../types'

export type ServiceContractType = 'AREA' | 'UNIT'

interface BaseServiceContract extends IdentifiableObject {
    service: number
    price: number | null
}
export interface ServiceContract extends BaseServiceContract {
    apartment: number
    vendor: number
    area_config?: number
    unit_config?: number
}

export interface PortfolioServiceContract extends BaseServiceContract {
    apartment: Company
    vendor: Company
    area_config: AreaConfig | null
    unit_config: UnitConfigAreaCounts | null
}

export interface UpstreamServiceContract extends BaseServiceContract {
    apartment: number
    area_config?: number
    unit_config?: number
}

export interface ServiceContractMap {
    [serviceId: number]: {
        areaConfig: { [configId: number]: number | string }
        unitConfig: { [configId: number]: number | string }
    }
}

export declare type BidStatusType =
    | 'PENDING'
    | 'APPROVED'
    | 'DENIED'
    | 'INCOMPLETE'

export enum RFPType {
    INVITE_ONLY = 'INVITE_ONLY',
    PUBLIC = 'PUBLIC',
    EZNOW = 'EZNOW',
}

export enum RFPStatus {
    DRAFT = 'DRAFT',
    PUBLISHED = 'PUBLISHED',
    CLOSED = 'CLOSED',
    ARCHIVED = 'ARCHIVED',
    FAILED = 'FAILED',
    SELECT_VENDOR = 'SELECT_VENDOR',
}

export interface RFPServiceDetail extends IdentifiableObject {
    question_description: string
    question_answer: string
    question_config?: number
}

export interface RFPService extends IdentifiableObject {
    service: Service
    service_details: RFPServiceDetail[]
}

export enum BidStatus {
    INVITATION = 'INVITATION',
    SUBMITTED = 'SUBMITTED',
    DECLINED = 'DECLINED',
    ACCEPTED = 'ACCEPTED',
    REVIEWED = 'REVIEWED',
}

export enum BidInviteTier {
    CURRENT = 'CURRENT',
    NEARBY = 'NEARBY',
    WILL_TRAVEL = 'WILL_TRAVEL',
}

export enum BidUnitScope {
    FULL = 'FULL',
    PARTIAL = 'PARTIAL',
}

export interface BidItem extends IdentifiableObject {
    price: number
    min_price?: string | null
    max_price?: string | null
    unit_scope?: BidUnitScope | null
    unit_config?: UnitConfig | null
    area_config?: BaseAreaConfig | null
}

export interface ServiceBid extends IdentifiableObject {
    rfp_service: number
    bid_items: BidItem[]
}

export interface Bid extends IdentifiableObject {
    vendor: NVLVendor
    status: BidStatus
    group_bid_price: number | null
    rfp: RFP
    vendor_answers: VendorAnswer[]
    bid_line_items: BidLineItem[]
    service_bids?: ServiceBid[]
    channel_id: number | null
    message_count: number
    is_unsolicited: boolean
    notes?: string
    invite_tier?: BidInviteTier | null
    distance_miles?: string | null
    has_vendor_coi?: boolean
    in_my_vendors?: boolean
}

export enum RFPDocumentType {
    PDF = 'PDF',
    DOC = 'DOC',
    DOCX = 'DOCX',
    JPG = 'JPG',
    JPEG = 'JPEG',
    PNG = 'PNG',
    XLSX = 'XLSX',
}

export interface RFPDocument {
    id: number
    file: string
    rfp: number
    public: boolean
    file_name: string
    file_type: RFPDocumentType
}

export enum ApartmentQuestionType {
    GENERAL = 'GENERAL',
    FINANCIAL = 'FINANCIAL',
}

export interface ApartmentQuestion extends IdentifiableObject {
    question_description: string
    type: ApartmentQuestionType
}

export interface VendorAnswer extends IdentifiableObject {
    apartment_question: number
    answer_description: string
}

export interface RFP extends IdentifiableObject {
    apartment: {
        id: number
        name: string
        primary_address: Address
        profile_picture: string | null
    }
    name: string
    project_submission_deadline: string | null
    project_start_date: string | null
    project_end_date: string | null
    type: RFPType
    status: RFPStatus
    budget: number
    rfp_services: RFPService[]
    description: string
    bids: Bid[]
    workorder_group: WorkorderGroup | null
    created_date: string
    accepted_bid: Bid | null
    rfp_documents: RFPDocument[]
    apartment_questions: ApartmentQuestion[]
    is_generated?: boolean
    turn_year?: number | null
    scope_snapshot?: GeneratedRFPScopeSnapshot | null
}

export interface GeneratedRFPUnitMix {
    unit_config_id: number
    name: string
    bed_count?: number | null
    bath_count?: number | null
    min_square_footage?: number | null
    max_square_footage?: number | null
    floorplan_image?: string | null
    full_count: number
    partial_count: number
    partial_vacant_beds: number
}

export type GeneratedRFPMixSource = 'historical' | 'tbd' | 'property'

export interface GeneratedRFPMixUnitType {
    unit_config_id: number
    name: string
    bed_count?: number | null
    bath_count?: number | null
    min_square_footage?: number | null
    max_square_footage?: number | null
    floorplan_image?: string | null
    inventory_count?: number
}

export interface GeneratedRFPMixGroup {
    bed_count: number | null
    bath_count: number | null
    label: string
    full_count: number | null
    partial_count: number | null
    source: GeneratedRFPMixSource
    unit_types: GeneratedRFPMixUnitType[]
}

export interface GeneratedRFPArea {
    area_config_id: number
    name: string
    area_type?: string | null
}

export interface GeneratedRFPInsurance {
    id: number
    name: string
    file: string | null
    ai_summary?: string | null
    certificate_holder_name?: string | null
    gl_each_occurrence?: string | null
    gl_general_aggregate?: string | null
    wc_statutory_limits?: boolean
}

export interface GeneratedRFPVendor {
    id: number
    name: string
    email: string
    has_email: boolean
    city?: string | null
    state?: string | null
    distance_miles?: number | null
    will_travel: boolean
    invite_tier: BidInviteTier
    is_trained?: boolean
    rating?: number
    tier?: 'silver' | 'gold' | 'blue' | 'dark_blue' | 'elite'
    go_back_rate?: number
    complete_count?: number
}

export interface GeneratedRFPServicePacket {
    key: 'paint' | 'clean'
    service_id: number | null
    service_name: string
    service_color?: string | null
    service_category?: ServiceCategoryCode | null
    existing_rfp_id: number | null
    existing_rfp_status: RFPStatus | null
    insurance: GeneratedRFPInsurance | null
    vendors: {
        current: GeneratedRFPVendor[]
        nearby: GeneratedRFPVendor[]
        will_travel: GeneratedRFPVendor[]
        has_origin: boolean
    }
}

export interface GeneratedRFPScopeSnapshot {
    year?: number
    unit_mix?: GeneratedRFPUnitMix[]
    mix_groups?: GeneratedRFPMixGroup[]
    mix_source?: GeneratedRFPMixSource
    history_years?: number[]
    areas?: GeneratedRFPArea[]
    move_out_histogram?: { date: string; count: number }[]
    move_out_date?: string | null
    move_in_date?: string | null
    reliable?: boolean
    insurance?: GeneratedRFPInsurance | null
    service_id?: number
    service_name?: string
    notes?: string
}

export interface GeneratedRFPPacket {
    year: number
    apartment_id: number
    apartment_name: string
    reliable: boolean
    move_out_date: string | null
    move_in_date: string | null
    move_out_count?: number
    move_out_share?: number
    move_in_count?: number
    move_in_share?: number
    project_start_date: string | null
    project_end_date: string | null
    project_end_source?: 'move_in' | 'prior_turn' | 'default_three_weeks' | null
    prior_turn_days?: number | null
    submission_deadline: string
    move_out_histogram: { date: string; count: number }[]
    unit_mix: GeneratedRFPUnitMix[]
    mix_groups?: GeneratedRFPMixGroup[]
    mix_source?: GeneratedRFPMixSource
    history_years?: number[]
    areas?: GeneratedRFPArea[]
    services: GeneratedRFPServicePacket[]
}

export interface BidLineItem extends IdentifiableObject {
    apartment_question: number | null
    rfp_service: number | null
    description: string
    price: number
}

export type ServiceContractCompoundKey = {
    serviceId: number
    vendorId: number
    areaConfigId: number | null
    unitConfigId: number | null
}

export enum PricingContractStatus {
    AWAITING_VENDOR = 'AWAITING_VENDOR',
    AWAITING_REVIEW = 'AWAITING_REVIEW',
    CURRENT = 'CURRENT',
    PAST = 'PAST',
    REJECTED = 'REJECTED',
}

export interface PricingContracts {
    id: number
    status: PricingContractStatus
    start_date: string
    year: number
    service_contracts: VendorServiceContract[]
    modified_date: string
    created_date: string
}

export interface VendorServiceContract {
    id: number
    apartment: number
    vendor: number
    service: number
    service_name: string
    price: string
    area_config: number | null
    unit_config: number | null
    area_name: string | null
    unit_name: string | null
}

export interface ServiceContractGroup extends IdentifiableObject {
    service_contracts: VendorServiceContract[]
    status: PricingContractStatus
    start_date: string
    end_date: string | null
    year: number
    apartment: number
    vendor: ListVendor
    created_date: string
    modified_date: string
}
